import * as React from "react";
import { useEffect } from "react";
import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import UIcon, { IconProps } from "../../../lib/icons/untitled_ui/UIcon";
import { smoothTransition } from "../../../styles/animations";

type TabsActivationMode = "automatic" | "manual";
type TabsDirection = "ltr" | "rtl";
type TabsStyle = "primary" | "secondary";

/**
 * Configuration for a single tab item
 * @property key - Unique identifier for the tab
 * @property label - Text label for the tab
 * @property children - Content to render in the tab panel
 * @property disabled - Whether the tab is disabled
 * @property icon - Optional icon to display before the label
 * @property iconProps - Additional props for the icon
 * @property badge - Optional badge text/number to display
 * @property showPaywall - Whether to show a paywall indicator
 */
export type TabItem = {
  key: string;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
  icon?: IconProps["icon"];
  iconProps?: Omit<IconProps, "icon">;
  badge?: string | number;
  showPaywall?: boolean;
};

/**
 * Props for the Tabs component
 * @property defaultValue - Initial active tab (uncontrolled mode)
 * @property value - Active tab (controlled mode)
 * @property onChange - Callback when active tab changes
 * @property activationMode - Whether tabs activate on focus or manually
 * @property dir - Text direction for RTL support
 * @property items - Array of tab items
 * @property variant - Visual style variant
 * @property fullWidth - Whether tabs should take up full width of container
 */
type TabsProps = {
  defaultValue?: string;
  value?: string;
  onChange?: (key: string) => void;
  activationMode?: TabsActivationMode;
  dir?: TabsDirection;
  items: TabItem[];
  variant?: TabsStyle;
  fullWidth?: boolean;
};

const PayWallIcon = <UIcon icon="paywallStar" color="yellow400" size={16} />;

/**
 * Tabs component for organizing content into multiple sections that can be navigated between.
 * Built on top of Radix UI's Tabs primitive (https://www.radix-ui.com/primitives/docs/components/tabs)
 * for robust accessibility and keyboard navigation.
 *
 * Features:
 * - Controlled and uncontrolled modes
 * - RTL support
 * - Primary and secondary visual variants
 * - Support for icons, badges, and paywall indicators
 * - Automatic and manual activation modes
 * - Full keyboard navigation
 * - ARIA compliant
 *
 * @example
 * ```tsx
 * <Tabs
 *   defaultValue="tab1"
 *   items={[
 *     { key: "tab1", label: "Account", children: <AccountContent /> },
 *     { key: "tab2", label: "Settings", children: <SettingsContent />, badge: "3" },
 *     { key: "tab3", label: "Premium", children: <PremiumContent />, showPaywall: true }
 *   ]}
 * />
 * ```
 *
 * Keyboard Interactions:
 * - Tab: When focus moves to tabs, focuses active trigger
 * - Arrow Right/Down: Moves focus to next trigger
 * - Arrow Left/Up: Moves focus to previous trigger
 * - Home: Moves focus to first trigger
 * - End: Moves focus to last trigger
 *
 * @param props - The tabs component props
 * @param props.defaultValue - The initial active tab (uncontrolled mode)
 * @param props.value - The active tab (controlled mode)
 * @param props.onChange - Callback when active tab changes
 * @param props.activationMode - Whether tabs activate automatically on focus or manually
 * @param props.dir - Text direction for RTL support
 * @param props.items - Array of tab items
 * @param props.variant - Visual style variant ("primary" or "secondary")
 * @param props.fullWidth - Whether tabs should take up full width of container
 */

export default function Tabs({
  defaultValue,
  value,
  onChange,
  activationMode = "automatic",
  dir = "ltr",
  items,
  variant = "primary",
  fullWidth = false,
}: TabsProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const activeTabRef = React.useRef<HTMLButtonElement>(null);
  const [indicatorStyle, setIndicatorStyle] = React.useState({ width: 0, left: 0 });

  const handleValueChange = (newValue: string) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const currentValue = value ?? internalValue;

  // Calculate the indicator position
  useEffect(() => {
    if (activeTabRef.current) {
      const { width, left } = activeTabRef.current.getBoundingClientRect();
      const listLeft = activeTabRef.current.parentElement?.getBoundingClientRect().left ?? 0;
      setIndicatorStyle({ width, left: left - listLeft });
    }
  }, [currentValue, activeTabRef]);

  return (
    <Root
      defaultValue={defaultValue}
      value={currentValue}
      onValueChange={handleValueChange}
      dir={dir}
      activationMode={activationMode}
    >
      <List fullWidth={fullWidth}>
        {items.map((item) => (
          <Trigger
            key={item.key}
            value={item.key}
            disabled={item.disabled}
            ref={item.key === currentValue ? activeTabRef : undefined}
            aria-disabled={item.disabled}
            fullWidth={fullWidth}
          >
            <TriggerContent
              isActive={item.key === currentValue}
              disabled={item.disabled}
              variant={variant}
            >
              {item.icon && <UIcon icon={item.icon} size={16} {...item.iconProps} />}
              <Label>{item.label}</Label>
              {item.badge && <Badge>{item.badge}</Badge>}
              {item.showPaywall && PayWallIcon}
            </TriggerContent>
          </Trigger>
        ))}
        <Indicator style={indicatorStyle} variant={variant} />
      </List>
      {items.map((item) => (
        <Content key={item.key} value={item.key}>
          {item.children}
        </Content>
      ))}
    </Root>
  );
}

const Root = styled(TabsPrimitive.Root)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
});

const List = styled(TabsPrimitive.List, { shouldForwardProp: (prop) => isPropValid(prop) })<{
  fullWidth?: boolean;
}>(({ theme, fullWidth }) => ({
  display: "flex",
  alignItems: "center",
  gap: fullWidth ? "0" : "32px",
  position: "relative",
  height: "48px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  width: "100%",

  padding: "0 24px",
}));

const Trigger = styled(TabsPrimitive.Trigger, { shouldForwardProp: (prop) => isPropValid(prop) })<{
  fullWidth?: boolean;
}>(({ theme, fullWidth }) => ({
  all: "unset",
  position: "relative",
  minWidth: fullWidth ? "0" : "85px",
  flex: fullWidth ? "1" : "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:focus-visible": {
    borderRadius: "2px",
    border: `2px solid ${theme.colors.sysPrimaryDefault}`,
  },
}));

const Label = styled.span({
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22px",
});

const Badge = styled.span(({ theme }) => ({
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  width: "20px",
  height: "20px",
  borderRadius: "10px",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1,
  color: theme.colors.sysTextWhite,
  backgroundColor: theme.colors.sysPrimaryDefault,
}));

const TriggerContent = styled.div<{ isActive: boolean; disabled?: boolean; variant?: TabsStyle }>(
  ({ theme, isActive, disabled, variant }) => {
    const isPrimary = variant !== "secondary";

    const getBaseColor = () => {
      if (disabled) return theme.colors.sysTextInactive;
      if (!isActive) return theme.colors.sysTextSecondary;
      return isPrimary ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault;
    };

    const getInteractionColors = () => {
      if (disabled) {
        return {
          hover: theme.colors.sysTextInactive,
          active: theme.colors.sysTextInactive,
        };
      }

      if (isPrimary) {
        return {
          hover: isActive ? theme.colors.sysPrimaryDefault : theme.colors.sysPrimaryHover,
          active: theme.colors.sysPrimaryPressed,
        };
      }

      return {
        hover: theme.colors.sysTextDefault,
        active: theme.colors.sysTextBlack,
      };
    };

    const { hover, active } = getInteractionColors();

    return {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: disabled ? "not-allowed" : "pointer",
      color: getBaseColor(),
      "&:hover": {
        color: hover,
      },
      "&:active": {
        color: active,
      },
    };
  },
);

const Content = styled(TabsPrimitive.Content)({
  minWidth: "100%",
  flex: "1 1 auto",
  height: "100%",
  overflow: "auto",
});

const Indicator = styled.div<{ variant?: TabsStyle }>(({ theme, variant }) => ({
  position: "absolute",
  bottom: "-1px",
  height: "2px",
  backgroundColor:
    variant === "secondary" ? theme.colors.sysTextDefault : theme.colors.sysPrimaryDefault,
  transition: smoothTransition,
}));
